import { Drawer, Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import logo from '@assets/images/bundle-drop-logo.svg';
import { useTheme } from '@context/ThemeContext';
import { adminNavigationItems } from '@router/adminNavigation';
import styles from './Sidebar.module.css';

const { Sider } = Layout;

type Props = {
  collapsed: boolean;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
};

const Sidebar = ({ collapsed, isMobile = false, mobileOpen = false, onMobileClose }: Props) => {
  const location = useLocation();
  const { colors } = useTheme();

  const menuItems: MenuProps['items'] = adminNavigationItems.map(item => ({
    key: item.key,
    icon: item.icon,
    label: <Link to={item.key}>{item.label}</Link>,
  }));

  const menuKeys = menuItems?.map(item => item!.key as string) ?? [];
  const activeKey =
    menuKeys
      .filter(key => location.pathname.startsWith(key))
      .sort((a, b) => b.length - a.length)[0] || location.pathname;

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (isMobile && key) {
      onMobileClose?.();
    }
  };

  const renderSidebarContent = (isCollapsed: boolean) => (
    <>
      <div className={styles.brand}>
        {isCollapsed ? (
          <img className={styles.brandLogoCollapsed} src={logo} alt="Bundle Drop" />
        ) : (
          <>
            <img className={styles.brandLogo} src={logo} alt="Bundle Drop" />
            <span className={styles.brandText}>Intraface</span>
          </>
        )}
      </div>

      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[activeKey]}
        onClick={handleMenuClick}
        className={styles.menu}
        style={{ flex: 1, backgroundColor: colors.bg_500 }}
        items={menuItems}
      />
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="left"
        open={mobileOpen}
        onClose={onMobileClose}
        width={272}
        closable={false}
        className={styles.mobileDrawer}
        styles={{
          body: {
            padding: 0,
            background: colors.bg_500,
          },
          content: {
            background: colors.bg_500,
          },
        }}
      >
        <div className={styles.mobileSidebar}>{renderSidebarContent(false)}</div>
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={220}
      className={styles.sidebar}
      style={{
        background: colors.bg_500,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      {renderSidebarContent(collapsed)}
    </Sider>
  );
};

export default Sidebar;
